import type { MeaningfulNoticingCard } from "@/components/coach/sessionPrep/MeaningfulNoticingSection";
import { dedupeEvidenceLinks } from "@/lib/coachEvidenceResolver";
import type {
  CoachLensContext,
  MeetingFeedbackItem,
  PracticeSummaryItem,
  ReflectionItem,
  TranscriptItem,
} from "@/hooks/useCoachSessionPrepData";

const TOPIC_STOPWORDS = new Set([
  "about", "after", "again", "around", "because", "being", "between", "could", "discussion", "during",
  "focus", "from", "have", "into", "just", "meeting", "notes", "project", "regarding", "team", "that",
  "their", "there", "these", "this", "through", "today", "were", "with", "would",
]);

type MeaningSourceItem = { text: string; link: string | null; label: string; evidenceId?: string | null; createdAt?: string | null };

function isReviewed(latestEvidenceAt: string | null, lastSeenAt: string | null) {
  if (!latestEvidenceAt || !lastSeenAt) return false;
  const latest = new Date(latestEvidenceAt).getTime();
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(latest) || Number.isNaN(seen)) return false;
  return latest <= seen;
}

function bucketMeetingSize(attendeeCount: number | null): "1:1" | "small" | "large" | "unknown" {
  if (!attendeeCount || attendeeCount < 1) return "unknown";
  if (attendeeCount <= 2) return "1:1";
  if (attendeeCount <= 6) return "small";
  return "large";
}

function mean(values: number[]) {
  if (values.length === 0) return null;
  return values.reduce((sum, next) => sum + next, 0) / values.length;
}

function confidenceFromSample(n: number): "low" | "medium" | "high" {
  if (n >= 10) return "high";
  if (n >= 5) return "medium";
  return "low";
}

function firstSentence(value?: string | null) {
  if (!value) return "";
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const match = normalized.match(/(.+?[.!?])(\s|$)/);
  return match?.[1]?.trim() || normalized;
}

function coachLensLine(coachLens: CoachLensContext | null) {
  return firstSentence(coachLens?.coaching_philosophy)
    || firstSentence(coachLens?.voice_summary)
    || firstSentence(coachLens?.frameworks)
    || "";
}

function buildMeaningTags(sourceItems: MeaningSourceItem[]) {
  const checks: Array<{ id: string; title: string; hint: string; prompt: string; patterns: RegExp[] }> = [
    {
      id: "tension",
      title: "Tension",
      hint: "Competing pressure, defensiveness, or conflict appears repeatedly.",
      prompt: "Where does tension show up most, and what shifts your leader's stance?",
      patterns: [/tension/i, /conflict/i, /defensive/i, /pushback/i, /hard/i],
    },
    {
      id: "alignment",
      title: "Alignment",
      hint: "Shared direction and role clarity appears uneven across meetings.",
      prompt: "What helps your leader create alignment sooner in the room?",
      patterns: [/align/i, /buy[- ]?in/i, /shared/i, /clarity/i],
    },
    {
      id: "decision_clarity",
      title: "Decision Clarity",
      hint: "Decision ownership or follow-through language is inconsistent.",
      prompt: "Which decision moments are still fuzzy after the meeting?",
      patterns: [/decision/i, /owner/i, /follow[- ]?up/i, /unclear/i],
    },
    {
      id: "stakeholder_risk",
      title: "Stakeholder Attention",
      hint: "Stakeholder dynamics may need closer coach review in some contexts.",
      prompt: "Which stakeholder contexts need more careful review for this leader?",
      patterns: [/stakeholder/i, /escalat/i, /risk/i, /cross[- ]functional/i],
    },
    {
      id: "emotional_load",
      title: "Emotional Load",
      hint: "Energy and emotional strain markers are present in the evidence.",
      prompt: "What emotional load is the leader carrying into these meetings?",
      patterns: [/overwhelm/i, /stress/i, /anxious/i, /friction/i, /fatigue/i],
    },
  ];

  return checks
    .map((item) => {
      const matchesByItem = sourceItems.map((source) => {
        const count = item.patterns.reduce((total, pattern) => total + (source.text.match(pattern)?.length || 0), 0);
        return { source, count };
      });
      const totalMatches = matchesByItem.reduce((sum, entry) => sum + entry.count, 0);
      const matchedSources = matchesByItem.filter((entry) => entry.count > 0).map((entry) => entry.source);
      return { ...item, totalMatches, matchedSources };
    })
    .filter((item) => item.totalMatches > 0 && item.matchedSources.length > 0);
}

export function buildCoachSessionPrepNoticing(params: {
  leaderId?: string;
  practiceSummaries: PracticeSummaryItem[];
  meetingFeedback: MeetingFeedbackItem[];
  reflections: ReflectionItem[];
  transcripts: TranscriptItem[];
  coachLens: CoachLensContext | null;
  lastSeenAt?: string | null;
}): MeaningfulNoticingCard[] {
  const { leaderId, practiceSummaries, meetingFeedback, reflections, transcripts, coachLens, lastSeenAt = null } = params;

  const feedbackByMeeting = new Map<string, MeetingFeedbackItem>();
  meetingFeedback.forEach((item) => feedbackByMeeting.set(item.meeting_id, item));

  const meetingInsightCards: MeaningfulNoticingCard[] = [];
  const meetingRows = practiceSummaries
    .map((item) => {
      const feedback = feedbackByMeeting.get(item.meeting_id);
      const evidenceId = item.transcript_upload_id ? `transcript-${item.transcript_upload_id}` : null;
      return {
        meetingId: item.meeting_id,
        rating: item.reflection_rating,
        sizeBucket: bucketMeetingSize(feedback?.attendee_count ?? null),
        typeBucket: feedback?.meeting_type?.toLowerCase() || "unknown",
        link: item.meeting_id ? `/coach/leaders/${leaderId}?section=evidence&meetingId=${item.meeting_id}` : `/coach/leaders/${leaderId}?section=evidence`,
        evidenceId,
      };
    })
    .filter((row) => typeof row.rating === "number") as Array<{
      meetingId: string;
      rating: number;
      sizeBucket: string;
      typeBucket: string;
      link: string;
      evidenceId: string | null;
    }>;

  const small = meetingRows.filter((row) => row.sizeBucket === "small" || row.sizeBucket === "1:1");
  const large = meetingRows.filter((row) => row.sizeBucket === "large");
  if (small.length >= 3 && large.length >= 3) {
    const smallMean = mean(small.map((row) => row.rating));
    const largeMean = mean(large.map((row) => row.rating));
    if (smallMean !== null && largeMean !== null) {
      const diff = Number((smallMean - largeMean).toFixed(2));
      meetingInsightCards.push({
        id: "meeting-size-pattern",
        notice: diff >= 0
          ? `Observed stronger self-ratings in smaller meetings than large meetings (${smallMean.toFixed(1)} vs ${largeMean.toFixed(1)}).`
          : `Observed stronger self-ratings in large meetings than smaller meetings (${largeMean.toFixed(1)} vs ${smallMean.toFixed(1)}).`,
        whyItMayMatter: "This pattern may indicate context sensitivity in how the leader experiences their presence and effectiveness.",
        whatToExplore: "Which meeting conditions help the leader stay grounded when room complexity increases?",
        confidence: confidenceFromSample(Math.min(small.length, large.length)),
        sampleSize: small.length + large.length,
        reviewed: isReviewed(
          [...small, ...large]
            .map((row) => practiceSummaries.find((item) => item.meeting_id === row.meetingId)?.created_at || null)
            .filter((value): value is string => Boolean(value))
            .sort()
            .at(-1) || null,
          lastSeenAt,
        ),
        evidence: dedupeEvidenceLinks([
          { label: `Small/1:1 meetings (${small.length})`, link: small[0]?.link || null, evidenceId: small[0]?.evidenceId || null },
          { label: `Large meetings (${large.length})`, link: large[0]?.link || null, evidenceId: large[0]?.evidenceId || null },
        ]),
      });
    }
  }

  const oneToOne = meetingRows.filter((row) => row.typeBucket.includes("1:1") || row.typeBucket.includes("one"));
  const group = meetingRows.filter((row) => !row.typeBucket.includes("1:1") && row.typeBucket !== "unknown");
  if (oneToOne.length >= 3 && group.length >= 3) {
    const oneToOneMean = mean(oneToOne.map((row) => row.rating));
    const groupMean = mean(group.map((row) => row.rating));
    if (oneToOneMean !== null && groupMean !== null) {
      meetingInsightCards.push({
        id: "meeting-type-pattern",
        notice: `Observed rating difference between 1:1 and group contexts (${oneToOneMean.toFixed(1)} vs ${groupMean.toFixed(1)}).`,
        whyItMayMatter: "This may point to different cognitive or relational load across meeting types rather than a general capability change.",
        whatToExplore: "Where does the leader feel most stretched across group stakeholder dynamics?",
        confidence: confidenceFromSample(Math.min(oneToOne.length, group.length)),
        sampleSize: oneToOne.length + group.length,
        reviewed: isReviewed(
          [...oneToOne, ...group]
            .map((row) => practiceSummaries.find((item) => item.meeting_id === row.meetingId)?.created_at || null)
            .filter((value): value is string => Boolean(value))
            .sort()
            .at(-1) || null,
          lastSeenAt,
        ),
        evidence: dedupeEvidenceLinks([
          { label: `1:1 contexts (${oneToOne.length})`, link: oneToOne[0]?.link || null, evidenceId: oneToOne[0]?.evidenceId || null },
          { label: `Group contexts (${group.length})`, link: group[0]?.link || null, evidenceId: group[0]?.evidenceId || null },
        ]),
      });
    }
  }

  const sourceItems = [
    ...practiceSummaries.map((item) => ({
      text: `${item.transcript_summary || ""} ${item.leader_reflection_text || ""}`,
      link: `/coach/leaders/${leaderId}?section=evidence&meetingId=${item.meeting_id}`,
      evidenceId: item.transcript_upload_id ? `transcript-${item.transcript_upload_id}` : null,
      label: item.meeting_title || "Practice summary",
      createdAt: item.created_at,
    })),
    ...reflections.map((item) => ({
      text: item.text || "",
      link: item.link,
      evidenceId: `reflection-${item.id}`,
      label: item.title,
      createdAt: item.created_at,
    })),
  ].filter((item) => item.text.trim().length > 0);

  const taxonomyNoticingCards = buildMeaningTags(sourceItems).map((tag) => ({
    id: `core5-${tag.id}`,
    notice: `${tag.title} appears repeatedly in recent evidence (${tag.totalMatches} matched markers across ${tag.matchedSources.length} evidence items).`,
    whyItMayMatter: tag.hint,
    whatToExplore: tag.prompt,
    confidence: confidenceFromSample(tag.matchedSources.length),
    sampleSize: tag.matchedSources.length,
    reviewed: isReviewed(
      tag.matchedSources
        .map((item) => item.createdAt || null)
        .filter((value): value is string => Boolean(value))
        .sort()
        .at(-1) || null,
      lastSeenAt,
    ),
    evidence: dedupeEvidenceLinks(tag.matchedSources.slice(0, 3).map((item) => ({
      label: item.label || "Evidence",
      link: item.link || null,
      evidenceId: item.evidenceId || null,
    }))),
  }));

  const topicSeedTranscripts = (() => {
    const seen = new Set<string>();
    return transcripts.filter((item) => {
      const normalizedTitle = (item.title || "").trim().toLowerCase();
      const key = item.meeting_id || normalizedTitle || item.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  const topicMap = new Map<string, Array<{ title: string; link: string }>>();
  topicSeedTranscripts.forEach((item) => {
    const corpus = `${item.title} ${item.summary || ""}`.toLowerCase();
    const terms = Array.from(new Set(corpus.match(/[a-z][a-z-]{3,}/g) || []))
      .filter((term) => !TOPIC_STOPWORDS.has(term));
    terms.slice(0, 8).forEach((term) => {
      const bucket = topicMap.get(term) || [];
      bucket.push({ title: item.title, link: item.link });
      topicMap.set(term, bucket);
    });
  });

  const recurringTopicCards = Array.from(topicMap.entries())
    .filter(([, evidence]) => evidence.length >= 2)
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, 3)
    .map(([term, evidence], idx) => ({
      id: `topic-${term}-${idx}`,
      notice: `Recurring meeting focus on "${term}" appears across ${evidence.length} transcripts.`,
      whyItMayMatter: "Repeated topic pressure can shape how a leader shows up across contexts, especially under load.",
      whatToExplore: `When "${term}" comes up, what behavior shift do you most want the leader to notice and own?`,
      confidence: confidenceFromSample(evidence.length),
      sampleSize: evidence.length,
      reviewed: isReviewed(
        topicSeedTranscripts
          .filter((item) => evidence.some((entry) => entry.link === item.link))
          .map((item) => item.created_at)
          .sort()
          .at(-1) || null,
        lastSeenAt,
      ),
      evidence: dedupeEvidenceLinks(evidence.slice(0, 3).map((item) => ({
        label: item.title || "Transcript",
        link: item.link,
        evidenceId: item.link ? `transcript-${item.link.split("/").pop()}` : null,
      }))),
    }));

  const lens = coachLensLine(coachLens);
  return [...meetingInsightCards, ...taxonomyNoticingCards, ...recurringTopicCards]
    .filter((card) => card.notice && card.whyItMayMatter && card.whatToExplore && card.evidence.length > 0)
    .map((card) => ({
      ...card,
      evidence: dedupeEvidenceLinks(card.evidence),
      coachingLens: lens || undefined,
      overallAssessment: card.notice,
      keyInsight: card.whyItMayMatter,
      strengthsObserved: card.evidence.slice(0, 2).map((entry) => `Signal in: ${entry.label}.`),
      growthOpportunities: [card.whatToExplore],
      tryThisNext: `In your next coaching conversation, use your lens to probe this pattern directly: ${card.whatToExplore}`,
    }));
}
